import { useEffect, useState, useCallback, Fragment } from "react"
import { useNavigate } from "react-router-dom"
import type { AppShell } from "@/App"
import {
  listTrackings,
  trackShipment,
  listExpeditionsByTracking,
  invokeAction,
  type TrackingRow,
  type Expedition,
} from "@/lib/api"
import { Card, CardContent } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { ArrowLeft, Truck, Download, RefreshCw, Search, XCircle } from "lucide-react"

interface Props {
  shell: AppShell | null
}

type StatusFilter = "active" | "all"
// "online" = expedições geradas no "criar expedição" da app (têm registo
// local em `expeditions`); "all" = inclui as criadas fora da app no Hub.
type SourceFilter = "online" | "all"

const PAGE_SIZE = 50

function fmtDate(iso: string | null): string {
  if (!iso) return "—"
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" })
}


export function Tracking({ shell }: Props) {
  const navigate = useNavigate()
  const [rows, setRows] = useState<TrackingRow[]>([])
  const [expByTracking, setExpByTracking] = useState<Map<string, Expedition>>(new Map())
  const [status, setStatus] = useState<StatusFilter>("active")
  const [source, setSource] = useState<SourceFilter>("online")
  const [offset, setOffset] = useState(0)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Real-time detail (track_shipment) keyed by tracking_number.
  const [detail, setDetail] = useState<TrackingRow | null>(null)
  const [detailBusy, setDetailBusy] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!shell) return
    setLoading(true)
    try {
      const [list, exp] = await Promise.all([
        listTrackings(shell, { status, limit: PAGE_SIZE, offset }),
        listExpeditionsByTracking(shell),
      ])
      setRows(list.trackings)
      setTotal(list.pagination?.total ?? list.trackings.length)
      setExpByTracking(exp)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [shell, status, offset])

  useEffect(() => {
    void load()
  }, [load])

  async function openDetail(tn: string) {
    if (!shell) return
    setDetailBusy(tn)
    setDetail(null)
    try {
      const t = await trackShipment(shell, tn)
      setDetail(t)
    } catch (err) {
      shell.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : String(err) })
    } finally {
      setDetailBusy(null)
    }
  }

  function openLabel(tn: string) {
    const url = expByTracking.get(tn)?.label_pdf_url
    if (url) window.open(url, "_blank", "noopener")
  }

  async function cancelPickup(exp: Expedition) {
    if (!shell) return
    setDetailBusy("cancel:" + exp.tracking_number)
    try {
      const res = await invokeAction(shell, "cancel_pickup", { order_id: exp.order_id })
      if (!res.success) throw new Error(res.error ?? "Falha ao cancelar recolha")
      shell.bridge.toast({ variant: "success", description: "Recolha cancelada." })
      await load()
    } catch (err) {
      shell.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : String(err) })
    } finally {
      setDetailBusy(null)
    }
  }

  // "Online" hides Hub expeditions that weren't generated by this app — i.e.
  // those without a local `expeditions` row. Applied to the loaded page (Hub
  // pagination/total stay on the full status set).
  const visibleRows = source === "online"
    ? rows.filter((r) => expByTracking.has(r.tracking_number))
    : rows
  const hiddenCount = rows.length - visibleRows.length

  const page = Math.floor(offset / PAGE_SIZE) + 1
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center gap-3 border-b px-6 py-4">
        <Button variant="outline" size="sm" onClick={() => navigate("/")}>
          <ArrowLeft className="mr-1.5 size-3.5" /> Voltar
        </Button>
        <div className="flex items-center gap-3">
          <Truck className="size-5 text-muted-foreground" />
          <div>
            <h1 className="text-lg font-semibold leading-tight">Tracking</h1>
            <p className="text-xs text-muted-foreground">{total} expedição{total === 1 ? "" : "ões"}</p>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="inline-flex rounded-md border p-0.5">
            {(["online", "all"] as SourceFilter[]).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSource(s)}
                className={
                  "rounded px-3 py-1 text-sm font-medium transition-colors " +
                  (source === s ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")
                }
              >
                {s === "online" ? "Online" : "Todas origens"}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-md border p-0.5">
            {(["active", "all"] as StatusFilter[]).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => { setStatus(s); setOffset(0) }}
                className={
                  "rounded px-3 py-1 text-sm font-medium transition-colors " +
                  (status === s ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")
                }
              >
                {s === "active" ? "Ativas" : "Todas"}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={"mr-1.5 size-3.5" + (loading ? " animate-spin" : "")} />
            Atualizar
          </Button>
        </div>
      </header>

      <div className="flex-1 overflow-auto p-6">
        {error && (
          <Card className="mb-4 border-destructive/30 bg-destructive/5">
            <CardContent className="py-3 text-sm text-destructive">{error}</CardContent>
          </Card>
        )}

        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tracking</TableHead>
                <TableHead>Referência</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Última sincronização</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && rows.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">A carregar…</TableCell></TableRow>
              ) : visibleRows.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">{source === "online" ? "Sem encomendas online nesta página." : "Sem expedições."}</TableCell></TableRow>
              ) : (
                visibleRows.map((r) => {
                  const exp = expByTracking.get(r.tracking_number)
                  const hasLabel = !!exp?.label_pdf_url
                  const canCancelPickup = !!exp?.pickup_number && !exp?.pickup_cancelled_at
                  const isDetail = detail?.tracking_number === r.tracking_number
                  return (
                    <Fragment key={r.tracking_number}>
                      <TableRow>
                        <TableCell className="font-mono text-xs font-medium">{r.tracking_number}</TableCell>
                        <TableCell className="text-sm">{r.reference ?? "—"}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center rounded-md border bg-muted px-2 py-0.5 text-[11px] font-medium">
                              {r.status_description ?? r.status_code ?? "—"}
                            </span>
                            {/* "active" flag from the Hub — the open/closed
                                distinction (never inferred from status_code). */}
                            <span className={"inline-flex items-center gap-1 whitespace-nowrap text-[11px] " + (r.active ? "text-emerald-600" : "text-muted-foreground")}>
                              <span className={"size-1.5 rounded-full " + (r.active ? "bg-emerald-500" : "bg-slate-400")} />
                              {r.active ? "Ativa" : "Inativa"}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{fmtDate(r.last_synced_at)}</TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex gap-1.5">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={detailBusy === r.tracking_number}
                              onClick={() => void openDetail(r.tracking_number)}
                            >
                              <Search className="mr-1.5 size-3.5" />
                              {detailBusy === r.tracking_number ? "…" : "Detalhes"}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={!hasLabel}
                              title={hasLabel ? "Abrir etiqueta guardada" : "Sem etiqueta guardada"}
                              onClick={() => openLabel(r.tracking_number)}
                            >
                              <Download className="mr-1.5 size-3.5" />
                              Etiqueta
                            </Button>
                            {canCancelPickup && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive"
                                disabled={detailBusy === "cancel:" + r.tracking_number}
                                title="Cancelar a recolha (mantém o envio)"
                                onClick={() => void cancelPickup(exp!)}
                              >
                                <XCircle className="mr-1.5 size-3.5" />
                                {detailBusy === "cancel:" + r.tracking_number ? "…" : "Cancelar recolha"}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      {isDetail && detail && (
                        <TableRow className="bg-muted/40">
                          <TableCell colSpan={5} className="text-xs">
                            <div className="flex flex-wrap gap-x-6 gap-y-1">
                              <span><span className="text-muted-foreground">Estado:</span> {detail.status_description ?? detail.status_code ?? "—"}</span>
                              <span><span className="text-muted-foreground">Provider:</span> {detail.provider ?? "—"}</span>
                              <span><span className="text-muted-foreground">Ativo:</span> {detail.active ? "sim" : "não"}</span>
                              <span><span className="text-muted-foreground">Sincronizado:</span> {fmtDate(detail.last_synced_at)}</span>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  )
                })
              )}
            </TableBody>
          </Table>
        </Card>

        {/* Pagination */}
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Página {page} de {pageCount}
            {source === "online" && hiddenCount > 0 ? ` · ${hiddenCount} fora da app ocultas` : ""}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0 || loading}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={offset + PAGE_SIZE >= total || loading}
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
            >
              Seguinte
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
